import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiTemplateKey,
  BiTemplateSubjectOption,
  BiTemplateSummary,
} from '@euler/model/bi/template';
import {
  ChevronLeft,
  LayoutTemplate,
  UserRound,
  Users,
} from 'lucide-react-native';
import { memo, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { BiMenuRow } from '../../components/ui/BiMenuRow';
import { BiMenuSheet } from '../../components/ui/BiMenuSheet';
import { BiRetry } from '../../components/ui/BiRetry';
import { useBiTemplateCreate } from './functions/useBiTemplateCreate';

/** The template title's line height, which its icon centers on. */
const kTitleLine = 20;

/**
 * 从模板新建: pick a template — the caller's own set marked 推荐 — and
 * open the dashboard made from it. A manager's 我的看板 asks whom it is
 * about (为员工创建): any advisor or technician of the store.
 */
export const TemplateSheet = memo(
  ({
    visible,
    onClose,
    onCreated,
  }: {
    visible: boolean;
    onClose: () => void;
    /** Opens the new dashboard in place of this one. */
    onCreated: (dashboardId: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { state, retry, create, busy, error } = useBiTemplateCreate(visible);
    const [members, setMembers] = useState<BiTemplateSubjectOption[] | null>(
      null,
    );
    const visibleRef = useRef(visible);
    visibleRef.current = visible;
    useEffect(() => {
      if (!visible) setMembers(null);
    }, [visible]);

    return (
      <BiMenuSheet
        visible={visible}
        title={members ? '为员工创建我的看板' : '从模板新建'}
        // Not while a dashboard is being made: it opens when it is ready.
        onClose={busy ? () => undefined : onClose}
      >
        {run => {
          const make = (templateKey: BiTemplateKey, subjectMemberId?: number) =>
            void create({
              templateKey,
              ...(subjectMemberId === undefined ? {} : { subjectMemberId }),
            }).then(id => {
              if (!id) return;
              // Still open: close, then open the new dashboard once the
              // sheet is gone; already closed: open it now.
              if (visibleRef.current) run(() => onCreated(id))();
              else onCreated(id);
            });
          const pick = (template: BiTemplateSummary) =>
            template.key === BiTemplateKey.MyDashboard && template.subjects
              ? setMembers(template.subjects)
              : make(template.key);
          return (
            <>
              {error ? (
                <Typography.Text size={rpx(12)} color={aiColors.destructive}>
                  {error}
                </Typography.Text>
              ) : null}
              {state.kind !== 'ready' ? (
                <View
                  style={{ paddingVertical: rpx(16), alignItems: 'center' }}
                >
                  {state.kind === 'failed' ? (
                    <BiRetry onRetry={retry} />
                  ) : (
                    <ActivityIndicator color={aiColors.primary} />
                  )}
                </View>
              ) : members ? (
                <>
                  <BiMenuRow
                    icon={ChevronLeft}
                    label="返回模板"
                    onPress={() => setMembers(null)}
                  />
                  <ScrollView style={{ maxHeight: rpx(360) }}>
                    {members.map(member => (
                      <BiMenuRow
                        key={member.memberId}
                        icon={UserRound}
                        label={member.name}
                        disabled={busy}
                        onPress={() =>
                          make(BiTemplateKey.MyDashboard, member.memberId)
                        }
                      />
                    ))}
                  </ScrollView>
                </>
              ) : (
                state.templates.map(template => (
                  <Pressable
                    key={template.key}
                    accessibilityRole="menuitem"
                    accessibilityLabel={template.name}
                    disabled={busy}
                    onPress={() => pick(template)}
                    style={({ pressed }) => ({
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: rpx(10),
                      paddingVertical: rpx(10),
                      paddingHorizontal: rpx(4),
                      opacity: busy ? 0.4 : pressed ? 0.6 : 1,
                    })}
                  >
                    {/* As tall as the title's line, so the icon centers on it. */}
                    <View
                      style={{
                        height: rpx(kTitleLine),
                        justifyContent: 'center',
                      }}
                    >
                      {template.key === BiTemplateKey.MyDashboard &&
                      template.subjects ? (
                        <Users size={16} color={aiColors.foreground} />
                      ) : (
                        <LayoutTemplate size={16} color={aiColors.foreground} />
                      )}
                    </View>
                    <View style={{ flex: 1, gap: rpx(2) }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: rpx(6),
                        }}
                      >
                        <Typography.Text
                          size={rpx(14)}
                          lineHeight={rpx(kTitleLine)}
                          color={aiColors.foreground}
                        >
                          {template.name}
                        </Typography.Text>
                        {template.recommended ? (
                          <Typography.Text
                            size={rpx(11)}
                            color={aiColors.primary}
                          >
                            推荐
                          </Typography.Text>
                        ) : null}
                      </View>
                      <Typography.Text
                        size={rpx(12)}
                        color={aiColors.mutedForeground}
                      >
                        {template.key === BiTemplateKey.MyDashboard &&
                        template.subjects
                          ? '为员工创建：选择一位服务顾问或技师'
                          : template.description}
                      </Typography.Text>
                    </View>
                  </Pressable>
                ))
              )}
              {busy ? (
                <View style={{ paddingVertical: rpx(8), alignItems: 'center' }}>
                  <ActivityIndicator color={aiColors.primary} />
                </View>
              ) : null}
            </>
          );
        }}
      </BiMenuSheet>
    );
  },
);
